import { useState } from 'react';
import { useLoaderData, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Icon, KpiCard, Progress } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { Menu, Pagination, Tabs } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { moeda } from '@/comuns/formato';
import { quantidade, unidadeAbrev } from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CargaDeProcedimentos, Procedimento } from '@/data/tipos';
import { DetalheDoProcedimento } from './procedimentos/DetalheDoProcedimento';
import { ProcedimentoModal } from './procedimentos/ProcedimentoModal';

const POR_PAGINA = 10;

export async function carregarProcedimentos({ request }: LoaderFunctionArgs): Promise<CargaDeProcedimentos> {
    const url = new URL(request.url);
    return dados().procedimentos({
        busca: url.searchParams.get('q') ?? undefined,
        categoria: url.searchParams.get('categoria') ?? undefined,
        pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
        porPagina: POR_PAGINA,
    });
}

export function Procedimentos() {
    const carga = useLoaderData() as CargaDeProcedimentos;
    const [params, setParams] = useSearchParams();
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [aberto, setAberto] = useState<Procedimento | null>(null);
    const [emEdicao, setEmEdicao] = useState<Procedimento | 'novo' | null>(null);

    const categoria = params.get('categoria') ?? '';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            if (chave !== 'pagina') proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    const reservar = async (procedimento: Procedimento) => {
        try {
            const reserva = await dados().reservarInsumos(procedimento.id, { origem: 'Reserva manual', paciente: 'Reserva manual' });
            /* A reserva insuficiente é criada e marcada: o aviso precisa dizer
               isso, senão a recepção sai achando que o insumo está garantido. */
            aviso(reserva.status === 'insuficiente'
                ? {
                    title: 'Reservado com insumo insuficiente',
                    description: `${procedimento.nome} · o estoque não cobre uma execução`,
                    tone: 'warning',
                }
                : { title: 'Insumos reservados', description: `${procedimento.nome} · veja em Estoque › Reservas` });
            setAberto(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível reservar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<Procedimento>[] = [
        {
            key: 'nome', header: 'Procedimento', sortable: true,
            render: (p) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span
                        aria-hidden style={{ width: 10, height: 10, flex: '0 0 auto', borderRadius: '50%', background: p.cor ?? 'var(--border-strong)' }}
                    />
                    <span>
                        <strong style={{ display: 'block', fontWeight: 600 }}>{p.nome}{!p.ativo && ' (inativo)'}</strong>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                            {[p.categoria, `${p.duracaoMin} min`, `${p.insumos.length} ${p.insumos.length === 1 ? 'insumo' : 'insumos'}`].filter(Boolean).join(' · ')}
                        </span>
                    </span>
                </span>
            ),
        },
        {
            key: 'profissionais', header: 'Profissionais',
            render: (p) => (p.profissionais.length
                ? p.profissionais.map((prof) => prof.nome).join(' · ')
                : <span style={{ color: 'var(--text-tertiary)' }}>sem vínculo</span>),
        },
        {
            key: 'custoDeInsumosCentavos', header: 'Custo', align: 'right',
            render: (p) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moeda(p.custoDeInsumosCentavos + p.custoExtraCentavos)}</span>
                    {p.custoExtraCentavos > 0 && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{moeda(p.custoExtraCentavos)} fora do estoque</span>
                    )}
                </span>
            ),
        },
        {
            key: 'precoCentavos', header: 'Valor', align: 'right',
            render: (p) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <strong style={{ fontWeight: 600 }}>{moeda(p.precoCentavos)}</strong>
                    {p.tipoValor !== 'fixo' && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{p.tipoValor === 'variavel' ? 'variável' : 'sob consulta'}</span>
                    )}
                </span>
            ),
        },
        {
            key: 'margem', header: 'Margem', align: 'right',
            render: (p) => (p.precoCentavos === 0 ? <span style={{ color: 'var(--text-tertiary)' }}>sem preço</span> : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
                    <Progress value={Math.max(0, p.margem)} size="sm" style={{ flex: 1 }} tone={p.margem >= 70 ? 'success' : p.margem >= 40 ? 'warning' : 'danger'} />
                    <span style={{ width: 38, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{p.margem}%</span>
                </span>
            )),
        },
        {
            key: 'execucoesPossiveis', header: 'Estoque suporta', align: 'right',
            render: (p) => {
                if (p.execucoesPossiveis === null) return <span style={{ color: 'var(--text-tertiary)' }}>sem insumo</span>;
                const n = p.execucoesPossiveis;
                return <Badge tone={n === 0 ? 'danger' : n < 3 ? 'warning' : 'success'} dot>{n} {n === 1 ? 'execução' : 'execuções'}</Badge>;
            },
        },
        {
            key: 'acoes', header: '', align: 'right', width: 60,
            render: (p) => (
                <span data-parar-linha>
                    <Menu
                        align="right" width={210}
                        onSelect={(id) => {
                            if (id === 'ver') setAberto(p);
                            else if (id === 'editar') setEmEdicao(p);
                            else void reservar(p);
                        }}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Mais ações — ${p.nome}`} />}
                        items={[
                            { id: 'ver', label: 'Ver insumos', icon: 'list' },
                            { id: 'editar', label: 'Editar procedimento', icon: 'pencil' },
                            { divider: true },
                            { id: 'reservar', label: 'Reservar insumos', icon: 'package-check', disabled: p.insumos.length === 0 },
                        ]}
                    />
                </span>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Procedimentos"
                description="Catálogo de serviços com valor, duração e os insumos que saem do estoque"
                actions={
                    <>
                        <ExportMenu<Procedimento>
                            label="Exportar" size="md"
                            payload={{
                                title: 'Procedimentos', subtitle: `${carga.total} procedimentos`, rows: carga.itens,
                                columns: [
                                    { header: 'Procedimento', value: (p) => p.nome },
                                    { header: 'Categoria', value: (p) => p.categoria ?? '' },
                                    { header: 'Duração', value: (p) => `${p.duracaoMin} min` },
                                    { header: 'Profissionais', value: (p) => p.profissionais.map((prof) => prof.nome).join(' · ') },
                                    { header: 'Custo de insumos', value: (p) => moeda(p.custoDeInsumosCentavos) },
                                    { header: 'Custos extras', value: (p) => moeda(p.custoExtraCentavos) },
                                    { header: 'Valor', value: (p) => moeda(p.precoCentavos) },
                                    { header: 'Margem', value: (p) => (p.precoCentavos === 0 ? '' : `${p.margem}%`) },
                                    { header: 'Insumos', value: (p) => p.insumos.map((i) => `${quantidade(i.quantidade)} ${unidadeAbrev(i.unidadeConsumo)} ${i.produto}`).join(' | ') },
                                ],
                            }}
                        />
                        <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setEmEdicao('novo')}>Novo procedimento</Button>
                    </>
                }
            >
                {/* As categorias são filtro, não rota: elas nascem do cadastro e
                    mudam com ele — endereço fixo para cada uma envelheceria na
                    primeira categoria renomeada. */}
                <Tabs
                    ariaLabel="Categorias do catálogo" activeId={categoria || 'todos'}
                    onChange={(id) => trocarParam('categoria', id === 'todos' ? null : id)}
                    tabs={[{ id: 'todos', label: 'Todos', count: carga.totais.procedimentos }]
                        .concat(carga.categorias.map((c) => ({ id: c.nome, label: c.nome, count: c.total })))}
                />
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            {carga.bloqueados.length > 0 && (
                <Alert
                    tone="warning" dismissible={false}
                    title={`${carga.bloqueados.length} ${carga.bloqueados.length === 1 ? 'procedimento sem insumo suficiente' : 'procedimentos sem insumo suficiente'}`}
                >
                    {carga.bloqueados.map((p) => p.nome).join(' · ')} — o estoque atual não cobre uma execução.
                </Alert>
            )}

            <FilterBar
                search={<SearchField value={params.get('q') ?? ''} onChange={(v) => trocarParam('q', v || null)} placeholder="Buscar procedimento" width={260} />}
                resultLabel={`${carga.total} ${carga.total === 1 ? 'procedimento' : 'procedimentos'}`}
            />

            <DataTable
                columns={colunas} rows={carga.itens} rowKey="id"
                onRowClick={setAberto} rowLabel={(p) => p.nome}
                emptyMessage="Nenhum procedimento encontrado."
            />

            <Pagination
                page={carga.pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA}
                onChange={(p) => trocarParam('pagina', String(p))}
            />

            <DetalheDoProcedimento
                procedimento={aberto}
                onClose={() => setAberto(null)}
                onEditar={(p) => { setAberto(null); setEmEdicao(p); }}
                onReservar={(p) => void reservar(p)}
            />

            <ProcedimentoModal
                alvo={emEdicao} produtos={carga.produtos} categorias={carga.categorias} profissionais={carga.profissionais}
                onClose={() => setEmEdicao(null)} onSalvo={() => revalidator.revalidate()}
            />
        </div>
    );
}
